import { LOCATION_INITIALIZED } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { InjectionToken, Injector } from '@angular/core';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';

import { Utils } from '../utils/common';
import { TranslateHttpLoader } from './translate-http-loader';

export function createTranslateLoader(
  http: HttpClient,
  cacheBustingString: string
) {
  const suffix = '.json?' + cacheBustingString;
  return new TranslateHttpLoader(http, '/assets/i18n/', suffix);
}

export const LCID_QUERYSTRING_PARAM_NAME = new InjectionToken<string>('lcid');
export const LCID_QUERYSTRING_PARAM_NAMES = new InjectionToken<string[]>(
  'lcid'
);

export let languagesDictionary = {
  '1069': { name: 'Basque-Basque', baseLocaleId: 'eu-ES', languageCode: 'eu' },
  '1026': {
    name: 'Bulgarian-Bulgaria',
    baseLocaleId: 'bg-BG',
    languageCode: 'bg',
  },
  '1027': {
    name: 'Catalan-Catalan',
    baseLocaleId: 'ca-ES',
    languageCode: 'ca',
  },
  '2052': {
    name: 'Chinese-China',
    baseLocaleId: 'zh-CN',
    languageCode: 'zh-cn',
  },
  '3076': {
    name: 'Chinese-Hong Kong',
    baseLocaleId: 'zh-HK',
    languageCode: 'zh',
  },
  '1028': {
    name: 'Chinese-Traditional',
    baseLocaleId: 'zh-TW',
    languageCode: 'zh',
  },
  '1050': {
    name: 'Croatian-Croatia',
    baseLocaleId: 'hr-HR',
    languageCode: 'hr',
  },
  '1029': {
    name: 'Czech - Czech Republic',
    baseLocaleId: 'cs-CZ',
    languageCode: 'cs',
  },
  '1030': { name: 'Danish-Denmark', baseLocaleId: 'da-DK', languageCode: 'da' },
  '1043': {
    name: 'Dutch-Netherlands',
    baseLocaleId: 'nl-NL',
    languageCode: 'nl',
  },
  '1033': { name: 'English', baseLocaleId: 'en-US', languageCode: 'en' },
  '1061': {
    name: 'Estonian-Estonia',
    baseLocaleId: 'et-EE',
    languageCode: 'et',
  },
  '1035': {
    name: 'Finnish-Finland',
    baseLocaleId: 'fi-FI',
    languageCode: 'fi',
  },
  '1036': { name: 'French-France', baseLocaleId: 'fr-FR', languageCode: 'fr' },
  '1110': { name: 'Galician-Spain', baseLocaleId: 'gl-ES', languageCode: 'gl' },
  '1031': { name: 'German-Germany', baseLocaleId: 'de-DE', languageCode: 'de' },
  '1032': { name: 'Greek-Greece', baseLocaleId: 'el-GR', languageCode: 'el' },
  '1081': { name: 'Hindi-India', baseLocaleId: 'hi-IN', languageCode: 'hi' },
  '1038': {
    name: 'Hungarian-Hungary',
    baseLocaleId: 'hu-HU',
    languageCode: 'hu',
  },
  '1057': {
    name: 'Indonesian-Indonesia',
    baseLocaleId: 'id-ID',
    languageCode: 'id',
  },
  '1040': { name: 'Italian-Italy', baseLocaleId: 'it-IT', languageCode: 'it' },
  '1041': { name: 'Japanese-Japan', baseLocaleId: 'ja-JP', languageCode: 'ja' },
  '1087': {
    name: 'Kazakh-Kazakhstan',
    baseLocaleId: 'kk-KZ',
    languageCode: 'en',
  }, //not supports in CKEditor
  '1042': { name: 'Korean-Korea', baseLocaleId: 'ko-KR', languageCode: 'ko' },
  '1062': { name: 'Latvian-Latvia', baseLocaleId: 'lv-LV', languageCode: 'lv' },
  '1063': {
    name: 'Lithuanian-Lithuania',
    baseLocaleId: 'lt-LT',
    languageCode: 'lt',
  },
  '1086': { name: 'Malay-Malaysia', baseLocaleId: 'ms-MY', languageCode: 'ms' },
  '1044': {
    name: 'Norwegian(Bokmal) - Norway',
    baseLocaleId: 'nb-NO',
    languageCode: 'nb',
  },
  '1045': { name: 'Polish-Poland', baseLocaleId: 'pl-PL', languageCode: 'pl' },
  '1046': {
    name: 'Portuguese-Brazil',
    baseLocaleId: 'pt-BR',
    languageCode: 'pt-br',
  },
  '2070': {
    name: 'Portuguese-Portugal',
    baseLocaleId: 'pt-PT',
    languageCode: 'pt',
  },
  '1048': {
    name: 'Romanian-Romania',
    baseLocaleId: 'ro-RO',
    languageCode: 'ro',
  },
  '1049': { name: 'Russian-Russia', baseLocaleId: 'ru-RU', languageCode: 'ru' },
  '3098': {
    name: 'Serbian (Cyrillic)-Serbia',
    baseLocaleId: 'sr-Cyrl-CS',
    languageCode: 'sr',
  },
  '2074': {
    name: 'Serbian (Latin)',
    baseLocaleId: 'sr-Latn-CS',
    languageCode: 'sr-latn',
  },
  '1051': {
    name: 'Slovak-Slovakia',
    baseLocaleId: 'sk-SK',
    languageCode: 'sk',
  },
  '1060': {
    name: 'Slovenian-Slovenia',
    baseLocaleId: 'sl-SI',
    languageCode: 'sl',
  },
  '3082': {
    name: 'Spanish (Traditional Sort)',
    baseLocaleId: 'es-ES',
    languageCode: 'es',
  },
  '1053': { name: 'Swedish-Sweden', baseLocaleId: 'sv-SE', languageCode: 'sv' },
  '1054': { name: 'Thai-Thailand', baseLocaleId: 'th-TH', languageCode: 'th' },
  '1055': { name: 'Turkish-Turkey', baseLocaleId: 'tr-TR', languageCode: 'tr' },
  '1058': {
    name: 'Ukrainian-Ukraine',
    baseLocaleId: 'uk-UA',
    languageCode: 'uk',
  },
  '1066': {
    name: 'Vietnamese-Vietnam',
    baseLocaleId: 'vi-VN',
    languageCode: 'vi',
  },
};

export const DEFAULT_LCID: string = '1033';

export function getLanguageCodeByUserLcid(userlcid: string): string {
  if (userlcid in languagesDictionary) {
    return languagesDictionary[userlcid].languageCode;
  }
  return languagesDictionary[DEFAULT_LCID].languageCode;
}

/**
 * initialize the languages resources. tries to download the requested language taken from the paramtere
 * on the query string.
 * translate - the translation service
 * injector - angular's injector
 * translateLoader - the supplied translate loader
 * queryStringLangParamName - the query string paramter to check for the language to load should be provided
 * as injectable token: LCID_QUERYSTRING_PARAM_NAME
 */
export function appTranslationsInitializerFactory(
  translate: TranslateService,
  injector: Injector,
  translateLoader: TranslateLoader,
  queryStringLangParamName: string
) {
  return () =>
    new Promise<any>((resolve: any) => {
      translate.setDefaultLang(DEFAULT_LCID);
      const locationInitialized = injector.get(
        LOCATION_INITIALIZED,
        Promise.resolve(null)
      );

      if (!queryStringLangParamName) {
        console.error(`appTranslationsInitializerFactory: queryStringLangParamName was not supplied. got: ${queryStringLangParamName}
      please check that you have defined: a provider for LCID_QUERYSTRING_PARAM_NAME`);
      }

      const userlcid = Utils.getParameterByName(queryStringLangParamName);

      if (!userlcid) {
        console.error(`appTranslationsInitializerFactory: could not locate the query string param: ${queryStringLangParamName}
      did you forget to add the language query string param on the link you are opening?`);
      }

      locationInitialized.then(() => {
        initTranslation(translate, userlcid, resolve);
      });
    });
}

// sdfasdf
export function appTranslationsInitializerFactoryV2(
  translate: TranslateService,
  injector: Injector,
  translateLoader: TranslateLoader,
  queryStringLangParamNames: string[]
) {
  return () =>
    new Promise<any>((resolve: any) => {
      translate.setDefaultLang(DEFAULT_LCID);
      const locationInitialized = injector.get(
        LOCATION_INITIALIZED,
        Promise.resolve(null)
      );

      if (!queryStringLangParamNames) {
        console.error(`appTranslationsInitializerFactory: queryStringLangParamName was not supplied. got: ${queryStringLangParamNames}
      please check that you have defined: a provider for LCID_QUERYSTRING_PARAM_NAME`);
      }

      // const userlcid = Utils.getParameterByName(queryStringLangParamName);
      const userlcid = Utils.getParameterByNames(
        queryStringLangParamNames,
        DEFAULT_LCID
      );

      if (!userlcid) {
        console.error(`appTranslationsInitializerFactory: could not locate the query string params: ${queryStringLangParamNames}
      did you forget to add the language query string param on the link you are opening?`);
      }

      locationInitialized.then(() => {
        // this language will be used as a fallback when a translation isn't found in the current language
        initTranslation(translate, userlcid, resolve);
      });
    });
}
function initTranslation(
  translate: TranslateService,
  userlcid: string,
  resolve: any
) {
  // this language will be used as a fallback when a translation isn't found in the current language
  translate.use(userlcid).subscribe(
    () => {
      if (
        Object.keys(translate.store.translations[translate.currentLang])
          .length !== 0
      ) {
        console.log(`Successfully initialized ${userlcid} language.`);
      } else {
        console.log(
          `Could not find the original language requested: ${userlcid} check if the default was loaded:`
        );
        if (
          Object.keys(translate.store.translations[translate.defaultLang])
            .length === 0
        ) {
          console.error(
            `Problem with loading the default lang ${translate.defaultLang}`
          );
        } else {
          console.log(`Loaded default lang ${translate.defaultLang}`);
        }
      }
    },
    (err: any) => {
      console.error(
        `Problem with '${userlcid}' language initialization. err: '` +
          err.toString()
      );
    },
    () => {
      resolve(null);
    }
  );
}
